"use client";

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";

export type MobileNavMode = "full" | "compact" | "hidden";

type MobileMenuContextType = {
  navMode: MobileNavMode;
  setNavMode: (mode: MobileNavMode) => void;
  /** True when the full tab bar is shown (not compact pill, not hidden). */
  isMenuExpanded: boolean;
  /** True when any bottom nav chrome is visible (full or compact). */
  isMenuVisible: boolean;
  /** Admin scroll-hide compat: toggles full ↔ hidden. */
  setIsMenuVisible: (visible: boolean) => void;
};

const defaultContextValue: MobileMenuContextType = {
  navMode: "full",
  setNavMode: () => {},
  isMenuExpanded: true,
  isMenuVisible: true,
  setIsMenuVisible: () => {},
};

const MobileMenuContext =
  createContext<MobileMenuContextType>(defaultContextValue);

export function MobileMenuProvider({ children }: { children: ReactNode }) {
  const [navMode, setNavMode] = useState<MobileNavMode>("full");

  const isMenuExpanded = navMode === "full";
  const isMenuVisible = navMode !== "hidden";

  const setIsMenuVisible = useCallback((visible: boolean) => {
    setNavMode(visible ? "full" : "hidden");
  }, []);

  return (
    <MobileMenuContext.Provider
      value={{
        navMode,
        setNavMode,
        isMenuExpanded,
        isMenuVisible,
        setIsMenuVisible,
      }}
    >
      {children}
    </MobileMenuContext.Provider>
  );
}

export function useMobileMenu() {
  return useContext(MobileMenuContext);
}
